import { useEffect } from 'react'
import { useAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  ChatErrorState,
  ChatHeaderState,
  chatErrorStateAtom,
  chatHeaderStateAtom,
} from '../atom'
import { societyQueries } from '../../../api/societies'
import { useAuth } from '../../../hooks/useAuth'
import { requestQueries } from '../../../api/requests'
import { ConversationType } from '../../../generated/api/chat-conversations'

interface useChatErrorStateProps {
  type: ConversationType
  isEmpty: boolean
  showSpinner: boolean
}

const EMPTY_STATE_BY_TYPE: Record<ConversationType, ChatErrorState> = {
  oneonone: ChatErrorState.EmptyChat,
  interests: ChatErrorState.EmptyGroupChat,
  board: ChatErrorState.EmptyBoardChat,
}

export const useChatErrorState = ({
  type,
  isEmpty,
  showSpinner,
}: useChatErrorStateProps): void => {
  const { id } = useParams()
  const [, setChatErrorState] = useAtom(chatErrorStateAtom)
  const [chatHeaderState] = useAtom(chatHeaderStateAtom)
  const { userId: authUserId } = useAuth()
  const userId = authUserId as string
  const { data: societies } = useQuery(societyQueries.getUserSocieties(userId))
  const isUserInAtleastOneSociety = societies && societies.length > 0
  const { data: userPendingRequests } = useQuery(
    requestQueries.getRequestsPerUser(userId)
  )
  const isOneOnOneChatDisabledForAllSocieties = societies?.every(
    (_society) => _society.settings?.chatOneOnOneDisabled
  )
  const isGroupChatDisabledForAllSocieties = societies?.every(
    (_society) => _society.settings?.chatInterestsDisabled
  )

  useEffect(() => {
    const chatIsEmpty = isUserInAtleastOneSociety && isEmpty && !showSpinner

    const pendingSocietyRequest =
      !isUserInAtleastOneSociety &&
      userPendingRequests &&
      userPendingRequests.length > 0

    const userHasNoSociety =
      !isUserInAtleastOneSociety &&
      userPendingRequests &&
      userPendingRequests.length === 0 &&
      !showSpinner

    const chatDisabled =
      !showSpinner &&
      ((type === 'oneonone' && isOneOnOneChatDisabledForAllSocieties) ||
        (type === 'interests' && isGroupChatDisabledForAllSocieties))

    if (
      chatHeaderState !== ChatHeaderState.None &&
      chatHeaderState !== ChatHeaderState.FindArchivedChats &&
      chatHeaderState !== ChatHeaderState.FindArchivedChatsResident
    ) {
      setChatErrorState(ChatErrorState.None)
      return
    }

    if (pendingSocietyRequest) {
      setChatErrorState(ChatErrorState.PendingRequest)
      return
    }

    if (userHasNoSociety) {
      setChatErrorState(ChatErrorState.NoSociety)
      return
    }

    if (chatDisabled) {
      setChatErrorState(ChatErrorState.ChatDisabled)
      return
    }

    if (chatIsEmpty) {
      setChatErrorState(EMPTY_STATE_BY_TYPE[type])
      return
    }

    if (!id) {
      setChatErrorState(ChatErrorState.NoChatSelected)
      return
    }

    setChatErrorState(ChatErrorState.None)
  }, [
    chatHeaderState,
    id,
    isEmpty,
    isGroupChatDisabledForAllSocieties,
    isOneOnOneChatDisabledForAllSocieties,
    isUserInAtleastOneSociety,
    setChatErrorState,
    showSpinner,
    type,
    userPendingRequests,
  ])
}
